import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Tooltip,
  IconButton,
  TextField,
  Button,
  Pagination,
  Typography,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
} from "@mui/material";
import { Search, Clear, Save } from "@mui/icons-material";
import apiUrl from "../api";

const PropertyTaxesTable = () => {
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProperties, setTotalProperties] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editedProperties, setEditedProperties] = useState({});
  const [savingProperty, setSavingProperty] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const user = JSON.parse(localStorage.getItem("user"));

  const showSnackbar = useCallback((message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  }, []);

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/property/getPropertiesNamesAndTaxes?page=${currentPage}&limit=${itemsPerPage}&search=${search}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setProperties(response.data.properties);
      setTotalProperties(response.data.pagination.total_items);
      setPageCount(response.data.pagination.total_pages);
    } catch (error) {
      console.error("Failed to fetch properties: ", error);
      showSnackbar("Failed to fetch properties", "error");
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, search, user.token, showSnackbar]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setCurrentPage(1);
    fetchProperties();
  };

  const handleClearSearch = () => {
    setSearch("");
    setCurrentPage(1);
    setTimeout(() => {
      fetchProperties();
    }, 0);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleTaxChange = (propertyId, field, value) => {
    // Ensure value is a number or empty string
    const numValue = value === "" ? "" : parseFloat(value);
    
    setEditedProperties(prev => ({
      ...prev,
      [propertyId]: {
        ...prev[propertyId],
        [field]: numValue
      }
    }));
  };

  const handleSaveProperty = async (property) => {
    const editedData = editedProperties[property._id];
    if (!editedData) {
      showSnackbar("No changes to save", "warning");
      return;
    }

    const taxLow = editedData.tax_low !== undefined ? editedData.tax_low : property.tax_low;
    const taxHigh = editedData.tax_high !== undefined ? editedData.tax_high : property.tax_high;

    if (taxLow === "" || taxHigh === "" || taxLow < 0 || taxHigh < 0) {
      showSnackbar("Please enter valid tax values", "error");
      return;
    }

    if (taxLow > taxHigh) {
      showSnackbar("Tax Low cannot be greater than Tax High", "error");
      return;
    }

    try {
      setSavingProperty(property._id);
      
      await axios.put(
        `${apiUrl}/property/updateTaxes`,
        {
          propertyId: property._id,
          taxLow: taxLow,
          taxHigh: taxHigh
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      // Update the property in the local state
      setProperties(prev => 
        prev.map(p => 
          p._id === property._id 
            ? { ...p, tax_low: taxLow, tax_high: taxHigh }
            : p
        )
      );

      // Remove from edited properties
      setEditedProperties(prev => {
        const newState = { ...prev };
        delete newState[property._id];
        return newState;
      });

      showSnackbar("Taxes updated successfully!", "success");
    } catch (error) {
      console.error("Failed to update taxes: ", error);
      const errorMessage = error.response?.data?.error || "Failed to update taxes";
      showSnackbar(errorMessage, "error");
    } finally {
      setSavingProperty(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getDisplayValue = (property, field) => {
    const editedData = editedProperties[property._id];
    if (editedData && editedData[field] !== undefined) {
      return editedData[field];
    }
    return property[field] || "";
  };

  const hasChanges = (propertyId) => {
    return editedProperties[propertyId] && Object.keys(editedProperties[propertyId]).length > 0;
  };

  return (
    <>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <form
            onSubmit={handleSearchSubmit}
            style={{
              display: "flex",
              flexDirection: "row",
              width: "70%",
              gap: "10px",
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search properties..."
              value={search}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <Tooltip title="Clear search">
                      <IconButton
                        onClick={handleClearSearch}
                        aria-label="clear search"
                      >
                        <Clear />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              style={{
                backgroundColor: "var(--primary-color)",
                color: "var(--primary-text-color)",
                fontSize: "15px",
                fontWeight: "500",
                minWidth: "100px",
              }}
            >
              Search
            </Button>
          </form>

          <FormControl variant="outlined" style={{ minWidth: 120 }}>
            <InputLabel>Items per page</InputLabel>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              label="Items per page"
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 600,
            overflow: "auto",
            width: "100%",
          }}
        >
          <Table
            aria-label="property taxes table"
            stickyHeader
            sx={{
              padding: "10px",
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold" }}>Property Name</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Tax Low</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Tax High</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} style={{ textAlign: "center", padding: "40px" }}>
                    <Typography>Loading properties...</Typography>
                  </TableCell>
                </TableRow>
              ) : properties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} style={{ textAlign: "center", padding: "40px" }}>
                    <Typography>No properties found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                properties.map((property) => (
                  <TableRow key={property._id}>
                    <TableCell>
                      <Typography variant="body2">{property.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={getDisplayValue(property, "tax_low")}
                        onChange={(e) => handleTaxChange(property._id, "tax_low", e.target.value)}
                        variant="outlined"
                        size="small"
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                        style={{ width: "120px" }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={getDisplayValue(property, "tax_high")}
                        onChange={(e) => handleTaxChange(property._id, "tax_high", e.target.value)}
                        variant="outlined"
                        size="small"
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                        style={{ width: "120px" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Save changes">
                        <span>
                          <Button
                            onClick={() => handleSaveProperty(property)}
                            disabled={!hasChanges(property._id) || savingProperty === property._id}
                            variant="contained"
                            size="small"
                            startIcon={<Save />}
                            style={{
                              backgroundColor: hasChanges(property._id) 
                                ? "var(--primary-color)" 
                                : "#ccc",
                              color: "white",
                              fontSize: "12px",
                            }}
                          >
                            {savingProperty === property._id ? "Saving..." : "Save"}
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          m={2}
          sx={{ width: "95%" }}
        >
          <Typography>
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalProperties)}-
            {Math.min(currentPage * itemsPerPage, totalProperties)} of{" "}
            {totalProperties} properties
          </Typography>
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={(event, page) => {
              setCurrentPage(page);
            }}
            color="primary"
            disabled={loading}
          />
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PropertyTaxesTable;
